<template>
  <!-- eslint-disable vue/no-v-html -->
  <span
    :class="['custom-icon', props.icon]"
    :style="rotate ? `transform: rotate(${rotate}deg)` : ''"
    v-bind="$attrs"
    v-html="svg"
  ></span>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import AiBrain from '@/components/core/icons/custom/ai-brain.svg?raw';
import Barbell from '@/components/core/icons/custom/barbell.svg?raw';
import CalendarDay from '@/components/core/icons/custom/calendar-day.svg?raw';
import ChartMultiple from '@/components/core/icons/custom/fluent_chart_multiple_24_regular.svg?raw';
import Clock from '@/components/core/icons/custom/clock.svg?raw';
import CodeBlock from '@/components/core/icons/custom/code-block.svg?raw';
import ColumnOutline from '@/components/core/icons/custom/column-outline.svg?raw';
import CustomFields from '@/components/core/icons/custom/custom-fields.svg?raw';
import BillingHistory from '@/components/core/icons/custom/billing-history.svg?raw';
import CustomSubscription from '@/components/core/icons/custom/custom-subscription.svg?raw';
import Donut from '@/components/core/icons/custom/donut.svg?raw';
import FolderArrow from '@/components/core/icons/custom/folder-arrow.svg?raw';
import FolderOutline from '@/components/core/icons/custom/folder-outline.svg?raw';
import HierarchyMap16 from '@/components/core/icons/custom/workstream/hierarchy-map-16.svg?raw';
import HierarchyMap24 from '@/components/core/icons/custom/workstream/hierarchy-map-24.svg?raw';
import New from '@/components/core/icons/custom/new.svg?raw';
import ItemNew from '@/components/core/icons/custom/item-new.svg?raw';
import Jira from '@/components/core/icons/custom/jira.svg?raw';
import ADO from '@/components/core/icons/custom/ado.svg?raw';
import Slack from '@/components/core/icons/custom/slack.svg?raw';
import People from '@/components/core/icons/custom/people.svg?raw';
import Percent from '@/components/core/icons/custom/percent.svg?raw';
import Priority from '@/components/core/icons/custom/priority.svg?raw';
import Shapes from '@/components/core/icons/custom/shapes.svg?raw';
import Sliders from '@/components/core/icons/custom/sliders.svg?raw';
import TextParagraph from '@/components/core/icons/custom/text-paragraph.svg?raw';
import Trello from '@/components/core/icons/custom/trello.svg?raw';
import WipLimit from '@/components/core/icons/custom/wip-limit.svg?raw';
import Magnet from '@/components/core/icons/custom/magnet.svg?raw';

// Workstream icons
import WorkstreamArrow from '@/components/core/icons/custom/workstream/workstream-arrow.svg?raw';
import WorkstreamCore from '@/components/core/icons/custom/workstream/workstream-core.svg?raw';
import WorkstreamEmpty from '@/components/core/icons/custom/workstream/workstream-empty.svg?raw';
import WorkstreamZero from '@/components/core/icons/custom/workstream/workstream-0.svg?raw';
import WorkstreamTen from '@/components/core/icons/custom/workstream/workstream-10.svg?raw';
import WorkstreamTwenty from '@/components/core/icons/custom/workstream/workstream-20.svg?raw';
import WorkstreamThirty from '@/components/core/icons/custom/workstream/workstream-30.svg?raw';
import WorkstreamForty from '@/components/core/icons/custom/workstream/workstream-40.svg?raw';
import WorkstreamFifty from '@/components/core/icons/custom/workstream/workstream-50.svg?raw';
import WorkstreamSixty from '@/components/core/icons/custom/workstream/workstream-60.svg?raw';
import WorkstreamSeventy from '@/components/core/icons/custom/workstream/workstream-70.svg?raw';
import WorkstreamEighty from '@/components/core/icons/custom/workstream/workstream-80.svg?raw';
import WorkstreamNinety from '@/components/core/icons/custom/workstream/workstream-90.svg?raw';
import WorkstreamHundred from '@/components/core/icons/custom/workstream/workstream-100.svg?raw';
import WorkstreamNew from '@/components/core/icons/custom/workstream/workstream-new.svg?raw';

const CustomIconsMap = {
  'ai-brain': AiBrain,
  barbell: Barbell,
  'calendar-day': CalendarDay,
  'chart-multiple': ChartMultiple,
  clock: Clock,
  'code-block': CodeBlock,
  'column-outline': ColumnOutline,
  donut: Donut,
  'workstream-arrow': WorkstreamArrow,
  'workstream-core': WorkstreamCore,
  'workstream-empty': WorkstreamEmpty,
  'workstream-0': WorkstreamZero,
  'workstream-10': WorkstreamTen,
  'workstream-20': WorkstreamTwenty,
  'workstream-30': WorkstreamThirty,
  'workstream-40': WorkstreamForty,
  'workstream-50': WorkstreamFifty,
  'workstream-60': WorkstreamSixty,
  'workstream-70': WorkstreamSeventy,
  'workstream-80': WorkstreamEighty,
  'workstream-90': WorkstreamNinety,
  'workstream-100': WorkstreamHundred,
  'workstream-new': WorkstreamNew,
  'custom-fields': CustomFields,
  'billing-history': BillingHistory,
  'custom-subscription': CustomSubscription,
  'folder-arrow': FolderArrow,
  'folder-outline': FolderOutline,
  'hierarchy-map-16': HierarchyMap16,
  'hierarchy-map-24': HierarchyMap24,
  jira: Jira,
  ado: ADO,
  new: New,
  'item-new': ItemNew,
  people: People,
  percent: Percent,
  priority: Priority,
  shapes: Shapes,
  sliders: Sliders,
  'wip-limit': WipLimit,
  'text-paragraph': TextParagraph,
  trello: Trello,
  slack: Slack,
  magnet: Magnet,
};

export type CustomIconName = keyof typeof CustomIconsMap;

interface Props {
  icon: CustomIconName;
  rotate?: string;
  size?: string;
}

const props = withDefaults(defineProps<Props>(), {
  rotate: '',
  size: '24px',
});

function replaceDimensions(svg: string) {
  return svg
    .replace(/<svg/, '<svg ')
    .replace(/width="\d+"/, `width="${props.size}"`)
    .replace(/height="\d+"/, `height="${props.size}"`);
}

const svg = computed(() => {
  if (!CustomIconsMap[props.icon] && import.meta.env.MODE !== 'production') {
    console.warn(
      `The ${props.icon} icon passed as a prop to the CustomIcon needs to be imported before it can be used. See the component code for details`
    );
  }
  return replaceDimensions(CustomIconsMap[props.icon]);
});
</script>
